import React, { useMemo, useRef } from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import NoDataAvailable from './NoDataAvailable';
import { fetchNoOfDefectsPerCycle } from '../../../../../modules/dashboard';
import { createQueryString, getRandomColor } from '../../../../../lib/utils';
import { Spinner } from 'react-bootstrap';
import ChartTitle from '../../../../elements/ChartTitle';

const GRAPH_TITLE = 'Defect Distribution By Test Cycle';

const getPriorityColor = (priority) => {
  const priorityColors = {
    Highest: '#a93226',
    High: '#e74c3c',
    Medium: '#d68910',
    Low: '#2e86c1',
    Lowest: '#2e4053',
    Critical: '#ba4a00',
  };

  return priorityColors[priority] || getRandomColor();
}
const IssuesByTestCycle = ({
  projectId,
  setShowExport,
  dateFilters,
  isJiraIntegrated,
  selectedJiraProject,
}) => {
  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);
  const jiraProjectId = selectedJiraProject?.linked_project_id;

  const queryKey = [
    'defectsByCycle',
    projectId,
    dateFilters,
    selectedJiraProject?.linked_project_id,
  ];

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [
    'defectsByCycle',
    projectId,
    dateFilters,
    selectedJiraProject?.linked_project_id,
  ],
    enabled: isJiraIntegrated && !!selectedJiraProject,
    queryFn: async () => {
      const queryString = createQueryString(dateFilters);
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;

      return fetchNoOfDefectsPerCycle(projectId, queryString, jiraProjectId, forceRefresh);
    },
    onSuccess: (res) => {
      if (res?.data?.length) {
        setShowExport(true);
      }
    },
    onError: (err) => {
      console.error('Failed to fetch data:', err);
    },
  });

  const data = response?.data || [];

  const priorities = useMemo(() => {
    const unique = new Set();
    data.forEach((cycle) => {
      (cycle.priority_breakdown || []).forEach(({ priority }) => {
        unique.add(priority);
      });
    });
    return [...unique];
  }, [data]);

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({ queryKey, exact: true });
  };

  return (
    <div className="chart-div">
      <ChartTitle
        title={GRAPH_TITLE}
        showWarning={!isJiraIntegrated}
        dateFilters={dateFilters}
        onRefresh={handleRefresh}
        isFetching={isFetching}
        showRefreshButton={!isLoading && isJiraIntegrated}
      />
      {isLoading ? (
        <Spinner className="mt-5" />
      ) : isError || data?.length === 0 ? (
        <NoDataAvailable />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            width={900}
            height={500}
            barSize={15}
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="test_cycle_name"
              label={{
                value: 'Test Cycles',
                position: 'insideBottom',
                offset: -10,
              }}
              tick={{ fontSize: 12 }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend wrapperStyle={{ padding: 10 }} />

            {priorities.map((priority) => (
              <Bar
                key={priority}
                dataKey={(entry) => {
                  const found = entry?.priority_breakdown.find(
                    (p) => p.priority === priority
                  );
                  return found ? found.count : 0;
                }}
                stackId="totalDefects"
                fill={getPriorityColor(priority)}
                name={priority}
              />
            ))}

            <Bar
              dataKey="new_defects_count"
              fill="#17a589"
              name="New Defects"
            />
            <Bar
              dataKey="existing_defects_count"
              fill="#2471a3"
              name="Existing Defects"
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default IssuesByTestCycle;
